import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FileText, Edit, Eye, ArrowLeft, Search,
  Info, Briefcase, GraduationCap, Award, Building2, Heart, Leaf, Users, Bell, Lightbulb 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';

const CMSManagement = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const pageIcons = {
    'about': Info,
    'career-guidance': GraduationCap,
    'job-fairs': Award,
    'job-notification': Bell,
    'entrepreneurship': Lightbulb,
    'job-facilitation-center': Building2,
    'education-support': GraduationCap,
    'medical-support': Heart,
    'environment-disaster-support': Leaf,
    'our-clients': Users,
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await api.get('/cms/pages');
      setPages(response.data);
    } catch (error) {
      toast.error('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const filteredPages = pages.filter(page => {
    const pageName = page.name || page.title || page.page_id || '';
    return pageName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getPageName = (page) => {
    return page.name || page.title || page.page_id?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Unknown Page';
  };

  const getPageId = (page) => {
    return page.id || page.page_id || '';
  };

  const getPageRoute = (page) => {
    return page.route || `/${page.page_id}` || '/';
  };

  const handlePreview = (route) => {
    window.open(route, '_blank');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>CMS Management</h1>
          <p className="text-slate-600 mt-1">Manage content for all information pages</p>
        </div>
      </div>

      {/* Search */}
      <Card className="p-4 mb-6 border-slate-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11"
          />
        </div>
      </Card>

      {/* Pages Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading pages...</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPages.map((page) => {
            const pageId = getPageId(page);
            const IconComponent = pageIcons[pageId] || FileText;
            return (
              <Card key={pageId} className="p-6 border-slate-200 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-teal-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{getPageName(page)}</h3>
                <p className="text-sm text-slate-500 mb-4">{getPageRoute(page)}</p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`/admin/cms/edit/${pageId}`)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handlePreview(getPageRoute(page))}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {filteredPages.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600">No pages found matching your search</p>
        </div>
      )}
    </div>
  );
};

export default CMSManagement;
